import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useSimulatorI18n } from "../i18n/simulator";
import { useShopI18n } from "../i18n/shop";
import { EDDY_I18N } from "../i18n/eddy";
import { useHardcodedI18n } from "../i18n/hardcoded";
// WalletMenuItems no longer imported — Vault/Burn/Governance/$REBEL items
// are now inlined inside the TOKEN $REBEL section of MenuOverlay (2026-02 reorg).
// The file is preserved at /app/frontend/src/components/WalletMenuItems.jsx
// in case another consumer reuses it; this module just doesn't need it anymore.

/**
 * Sofi-style fullscreen menu overlay.
 * Purely additive — coexists with the existing navbar.
 * Items are i18n-aware and route correctly inside the SPA.
 */
export const MenuOverlay = ({ open, onClose }) => {
  const { t, lang } = useLanguage();
  const tSim = useSimulatorI18n(lang);
  const tShop = useShopI18n(lang);
  const hc = useHardcodedI18n(lang);
  const { user, openLogin, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Items: smart routing depending on current route
  const goSection = (id) => {
    onClose();
    if (pathname !== "/") {
      navigate("/");
      // wait for landing render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = (document.querySelector("header")?.offsetHeight || 70) + 16;
          window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: "smooth" });
        }
      }, 80);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const offset = (document.querySelector("header")?.offsetHeight || 70) + 16;
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: "smooth" });
      }
    }
  };

  // Item registry — each entry has a STRING `key` (used as React key + data-testid
  // suffix) so we never reintroduce the duplicate-numeric-key bug. Sections are
  // pure visual grouping; routes/handlers/badges are untouched from the audit.
  const sections = [
    {
      title: (EDDY_I18N[lang] || EDDY_I18N.en || EDDY_I18N.es).nav_menu_item,
      items: [
        { key: "rebel-by-eddy", txt: (EDDY_I18N[lang] || EDDY_I18N.en || EDDY_I18N.es).nav_menu_item,  action: () => { onClose(); navigate("/rebel-by-eddy"); }, badge: (EDDY_I18N[lang] || EDDY_I18N.en || EDDY_I18N.es).nav_badge_new },
      ],
    },
    {
      title: t.nav.section_learn,
      items: [
        { key: "academy",       txt: t.nav.academy,                                    action: () => { onClose(); navigate("/academy"); } },
        { key: "learn-to-earn", txt: "LEARN-TO-EARN 🎓",                               action: () => { onClose(); navigate("/learn-to-earn"); }, badge: "EARN" },
        { key: "monetiza",      txt: hc.menu_monetiza,                                 action: () => { onClose(); navigate("/shadow/monetiza"); } },
      ],
    },
    {
      title: t.nav.section_trade,
      items: [
        { key: "simulator",     txt: tSim.nav_btn,          action: () => { onClose(); navigate("/simulator"); }, badge: tSim.nav_badge },
        { key: "liquidity-map", txt: t.nav.liquidity_map,   action: () => { onClose(); navigate("/liquidity-map"); } },
        { key: "trendia",       txt: t.nav.trendia,         action: () => goSection("trendia") },
        { key: "topwallet",     txt: t.nav.topwallet,       action: () => goSection("topwallet") },
      ],
    },
    {
      title: t.nav.section_community,
      items: [
        { key: "shadow-network", txt: "SHADOW NETWORK",                       action: () => { onClose(); navigate("/shadow"); },         badge: "ELITE" },
        { key: "shadow-market",  txt: `SHADOW MARKET — ${tShop.nav_shop}`,    action: () => { onClose(); navigate("/shadow-market"); }, badge: "WIP" },
      ],
    },
    {
      title: t.nav.section_token,
      items: [
        { key: "rebel",      txt: "$RE₿EL",         action: () => { onClose(); navigate("/shadow/tokens"); } },
        { key: "vault",      txt: t.nav.vault,      action: () => { onClose(); navigate("/vault"); } },
        { key: "governance", txt: t.nav.governance, action: () => { onClose(); navigate("/governance"); } },
        { key: "burn",       txt: t.nav.burn,       action: () => { onClose(); navigate("/vault#burn"); } },
      ],
    },
    {
      title: t.nav.section_tools,
      items: [
        { key: "listas", txt: hc.menu_listas, action: () => { onClose(); navigate("/shadow/listas"); } },
      ],
    },
  ];

  const isAuthed = user && user !== false;

  return (
    <div
      id="sofi-overlay"
      aria-hidden={!open}
      className={open ? "ov-open" : ""}
      data-testid="menu-overlay"
    >
      <div className="sofi-ov-bg" />
      <div className="sofi-ov-body">
        <button
          type="button"
          className="sofi-ov-close"
          aria-label="close menu"
          onClick={onClose}
          data-testid="menu-overlay-close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <nav className="sofi-ov-nav">
          {sections.map((sec) => (
            <div key={`sec-${sec.title}`} className="sofi-ov-section">
              <div
                className="sofi-ov-section-title"
                style={{
                  padding: "14px 4px 6px",
                  fontSize: "0.62rem",
                  fontWeight: 800,
                  letterSpacing: "0.22em",
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                }}
                data-testid={`menu-overlay-section-${sec.title}`}
              >
                {sec.title}
              </div>
              {sec.items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="sofi-ov-link"
                  data-i={it.key}
                  data-testid={`menu-overlay-link-${it.key}`}
                  onClick={it.action}
                >
                  <span className="ov-txt">{it.txt}</span>
                  {it.badge && (
                    <span
                      className="ov-badge"
                      style={{
                        background: "#00e676",
                        color: "#000",
                        padding: "2px 7px",
                        borderRadius: "999px",
                        fontSize: "0.55rem",
                        fontWeight: 900,
                        letterSpacing: "0.18em",
                        marginLeft: "10px",
                        textTransform: "uppercase",
                      }}
                    >
                      {it.badge}
                    </span>
                  )}
                  <span className="ov-arrow">→</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sofi-ov-footer">
          {isAuthed ? (
            <button
              type="button"
              className="ov-login"
              onClick={() => { onClose(); logout(); }}
              data-testid="menu-overlay-logout"
            >
              {t.nav.logout || "logout"}
            </button>
          ) : (
            <button
              type="button"
              className="ov-login"
              onClick={() => { onClose(); openLogin(); }}
              data-testid="menu-overlay-login"
            >
              {t.nav.login || "iniciar sesión"}
            </button>
          )}
          <button
            type="button"
            className="ov-cta"
            onClick={() => { onClose(); navigate("/academy"); }}
            data-testid="menu-overlay-cta"
          >
            {t.nav.academy} →
          </button>
        </div>

        <div className="sofi-ov-deco-line" />
      </div>
    </div>
  );
};

export default MenuOverlay;
